import { assertIsNotUndefined, type IAddress } from '~/types/models'
import { CountryIds } from '~/services/selectLists/countries'

const mn = '[cynqueModules/dataObjects/Address.ts]'

export function Address(
  o: IAddress,
  useSeparateLineForAddressLine2: boolean = true
): IAddress {
  function _descr() {
    return (
      _addressLines(useSeparateLineForAddressLine2) +
      '\n' +
      _cityStateZip() +
      (o.country.id === CountryIds.UNITED_STATES
        ? ''
        : '\n' + o.country.name.toUpperCase())
    )
  }

  function _shortDescr() {
    return (
      _addressLines(useSeparateLineForAddressLine2) +
      (o.city ? ', ' + o.city : '')
    )
  }

  function _addressLines(useSeparateLineForAddressLine2: boolean): string {
    const addressLine1AddressLine2Separator = useSeparateLineForAddressLine2
      ? '\n'
      : ', '

    const addressLines1And2 =
      (o.addressLine1 ? o.addressLine1 : '') +
      (o.addressLine1 && o.addressLine2
        ? addressLine1AddressLine2Separator
        : '') +
      (o.addressLine2 ? o.addressLine2 : '')

    return o.country.id === CountryIds.JAPAN
      ? addressLines1And2 + (o.city ? ', ' + o.city : '')
      : addressLines1And2
  }

  function _cityStateZip() {
    const fn = `${mn} _cityStateZip |`
    console.debug(`${fn} country is ${o.country.name}`, o.country)
    const isSupportedCountry = countryIdAddressFunctionMap.has(o.country.id)
    if (!isSupportedCountry) {
      console.error(
        `${fn} ERROR: no address formatter for country: '${o.country.name}'`,
        o.country
      )
      return '!!! NO FORMATTER FOR THIS COUNTRY!!!'
    }
    const countryFunction = countryIdAddressFunctionMap.get(o.country.id)
    assertIsNotUndefined(countryFunction, fn, o.country.name) // if it's in the map, should always be defined.
    return countryFunction()
  }

  // Country Id - Function Map

  const countryIdAddressFunctionMap = new Map<number, Function>([
    [CountryIds.AUSTRALIA, cityStateZip],
    [CountryIds.CANADA, cityStateZip],
    [CountryIds.DENMARK, zipCity],
    [CountryIds.FRANCE, zipCity],
    [CountryIds.HONG_KONG_SAR, cityLineBreakState],
    [CountryIds.GERMANY, zipCity],
    [CountryIds.INDIA, cityZipLineBreakState],
    [CountryIds.JAPAN, stateZip],
    [CountryIds.ITALY, zipCityState],
    [CountryIds.MEXICO, zipCityStateMexico],
    [CountryIds.NETHERLANDS, zipCity],
    [CountryIds.PHILIPPINES, cityLineBreakZipState],
    [CountryIds.POLAND, zipCity],
    [CountryIds.SINGAPORE, singaporeLiteralZip],
    [CountryIds.SWEDEN, zipCity],
    [CountryIds.SWITZERLAND, zipCity],
    [CountryIds.UNITED_KINGDOM, cityLineBreakZip],
    [CountryIds.UNITED_STATES, cityStateZip],
  ])

  // actual formatter functions

  function stateZip() {
    return (
      (o.state?.label ? o.state.label + ' ' : '') +
      (o.zip ? o.zip.toUpperCase() : '') // works for both U.S. and Canada
    )
  }

  function cityStateZip() {
    return (
      (o.city ? o.city : '') +
      ((o.city && o.state?.label) || (o.city && o.zip) ? ', ' : '') +
      (o.state?.label ? o.state.label + ' ' : '') +
      (o.zip ? o.zip.toUpperCase() : '') // works for both U.S. and Canada
    )
  }

  function cityLineBreakState() {
    return (
      (o.city ? o.city : '') +
      (o.city && o.state?.label ? '\n' : '') +
      (o.state?.label ? o.state.label : '')
    )
  }

  function zipCity() {
    return (
      (o.zip ? o.zip : '') +
      (o.zip && o.city ? ' ' : '') +
      (o.city ? o.city : '')
    )
  }

  function singaporeLiteralZip() {
    return 'Singapore' + (o.zip ? ' ' + o.zip : '')
  }

  function cityLineBreakZip() {
    return (
      (o.city ? o.city : '') +
      (o.city && o.zip ? '\n' : '') +
      (o.zip ? o.zip.toUpperCase() : '')
    )
  }

  function zipCityStateMexico() {
    return 'C.P. ' + zipCityState()
  }

  function zipCityState() {
    return (
      (o.zip ? o.zip : '') +
      (o.zip && o.city ? ' ' : '') +
      (o.city ? o.city : '') +
      (o.city && o.state?.label ? ' ' : '') +
      (o.state?.label ? o.state.label : '')
    )
  }

  function cityLineBreakZipState() {
    return (
      (o.city ? o.city + '\n' : '') +
      (o.zip ? o.zip : '') +
      (o.zip && o.state ? ' ' : '') +
      (o.state?.label ? o.state.label : '')
    )
  }

  function cityZipLineBreakState() {
    return (
      (o.city ? o.city : '') +
      (o.city && o.zip ? ' ' : '') +
      (o.zip ? o.zip : '') +
      (o.zip && o.state ? '\n' : '') +
      (o.state?.label ? o.state.label : '')
    )
  }

  //
  // Execution begins here
  //

  const fn = `${mn} Address |`

  if (!o) {
    throw new Error(`${fn} Address called with falsey address`)
  }

  const obj = { ...o }
  obj.descr = _descr()
  obj.shortDescr = _shortDescr()
  obj.addressLines = _addressLines(useSeparateLineForAddressLine2)
  obj.cityStateZip = _cityStateZip()
  return obj
}
